import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { portfolioItemsApi } from "./api";
import type { PortfolioItemFilters, PortfolioItemInput, PublicPortfolioItemFilters } from "./types";

export const portfolioItemKeys = {
  all: ["portfolio-items"] as const,
  list: (filters: PortfolioItemFilters) => ["portfolio-items", "list", filters] as const,
  detail: (id: string | null) => ["portfolio-items", "detail", id] as const,
  publicList: (filters: PublicPortfolioItemFilters) => ["portfolio-items", "public-list", filters] as const,
  publicDetail: (slug: string | null) => ["portfolio-items", "public-detail", slug] as const,
};

export function usePortfolioItems(filters: PortfolioItemFilters) {
  return useQuery({
    queryKey: portfolioItemKeys.list(filters),
    queryFn: () => portfolioItemsApi.list(filters),
  });
}

export function usePortfolioItem(id: string | null) {
  return useQuery({
    queryKey: portfolioItemKeys.detail(id),
    queryFn: () => portfolioItemsApi.detail(id!),
    enabled: Boolean(id),
  });
}

export function usePublicPortfolioItems(filters: PublicPortfolioItemFilters) {
  return useQuery({
    queryKey: portfolioItemKeys.publicList(filters),
    queryFn: () => portfolioItemsApi.publicList(filters),
  });
}

export function usePublicPortfolioItem(slug: string | null) {
  return useQuery({
    queryKey: portfolioItemKeys.publicDetail(slug),
    queryFn: () => portfolioItemsApi.publicDetail(slug!),
    enabled: Boolean(slug),
    retry: false,
  });
}

export function useCreatePortfolioItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ input, files, mediaIds = [] }: { input: PortfolioItemInput; files: File[]; mediaIds?: string[] }) =>
      portfolioItemsApi.create(input).then(async (item) => {
        if (files.length > 0) await portfolioItemsApi.uploadImages(item.id, files);
        if (mediaIds.length > 0) await portfolioItemsApi.attachMediaImages(item.id, mediaIds);
        return item;
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: portfolioItemKeys.all }),
  });
}

export function useUpdatePortfolioItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input, files, mediaIds = [] }: { id: string; input: PortfolioItemInput; files: File[]; mediaIds?: string[] }) =>
      portfolioItemsApi.update(id, input).then(async (item) => {
        if (files.length > 0) await portfolioItemsApi.uploadImages(item.id, files);
        if (mediaIds.length > 0) await portfolioItemsApi.attachMediaImages(item.id, mediaIds);
        return item;
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: portfolioItemKeys.all });
      queryClient.invalidateQueries({ queryKey: portfolioItemKeys.detail(variables.id) });
    },
  });
}

export function useDeletePortfolioItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: portfolioItemsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: portfolioItemKeys.all }),
  });
}

export function useSetPrimaryPortfolioImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, imageId }: { itemId: string; imageId: string }) =>
      portfolioItemsApi.updateImage(itemId, imageId, { isPrimary: true }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: portfolioItemKeys.all });
      queryClient.invalidateQueries({ queryKey: portfolioItemKeys.detail(variables.itemId) });
    },
  });
}
